
import { useState, useMemo, useEffect } from 'react';
import { FilterX, Search, ChevronDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { 
  FilterOptions, 
  NetworkProvider, 
  DataAllowance, 
  ContractLength, 
  RoamingType, 
  Plan, 
  TalkTime,
  ActiveFilter
} from '../types';
import { ActiveFilterTags } from './ActiveFilterTags';
import { CollapsibleFilters } from './CollapsibleFilters';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from '../hooks/use-mobile';

interface FilterSidebarProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onReset: () => void;
  className?: string;
  allPlans: Plan[];
  sortDataAllowances?: (dataAllowances: string[]) => string[];
}

export const FilterSidebar = ({ 
  filters, 
  onFiltersChange, 
  onReset,
  className = '',
  allPlans,
  sortDataAllowances
}: FilterSidebarProps) => {
  const [priceRange, setPriceRange] = useState<[number, number]>([
    filters.minPrice !== undefined ? filters.minPrice : 0, 
    filters.maxPrice !== undefined ? filters.maxPrice : 500
  ]);

  const isMobile = useIsMobile();
  const [displayMode, setDisplayMode] = useState<'sidebar' | 'horizontal'>(isMobile ? 'sidebar' : 'horizontal');
  
  // Set display mode based on screen size
  useEffect(() => {
    if (isMobile !== undefined) {
      setDisplayMode(isMobile ? 'sidebar' : 'horizontal');
    }
  }, [isMobile]);
  
  useEffect(() => {
    setPriceRange([
      filters.minPrice !== undefined ? filters.minPrice : 0,
      filters.maxPrice !== undefined ? filters.maxPrice : 500
    ]);
  }, [filters.minPrice, filters.maxPrice]);
  
  const activeFilters = useMemo(() => {
    const result: ActiveFilter[] = [];
    
    // Provider filters
    filters.providers.forEach(provider => {
      result.push({
        type: 'providers',
        value: provider,
        label: `Udbyder: ${provider}`
      });
    });
    
    // Data allowance filters
    filters.dataAllowances.forEach(data => {
      result.push({
        type: 'dataAllowances',
        value: data,
        label: `Data: ${data}`
      });
    });
    
    // Talk time filters
    filters.talkTimes.forEach(talkTime => {
      result.push({
        type: 'talkTimes',
        value: talkTime,
        label: `Taletid: ${talkTime}`
      });
    });
    
    // Roaming filters
    filters.roaming.forEach(roaming => {
      result.push({
        type: 'roaming',
        value: roaming,
        label: `Roaming: ${roaming}`
      });
    });
    
    // 5G filter
    if (filters.has5G !== undefined) {
      result.push({
        type: 'has5G',
        value: true,
        label: '5G Netværk'
      });
    }
    
    // Price range filter
    if (filters.minPrice !== undefined && filters.minPrice > 0 || 
        filters.maxPrice !== undefined && filters.maxPrice < 500) {
      result.push({
        type: 'priceRange',
        value: [filters.minPrice || 0, filters.maxPrice || 500],
        label: `Pris: ${filters.minPrice || 0}kr - ${filters.maxPrice || '500+'}kr`
      });
    }
    
    return result;
  }, [filters]);
  
  const availableOptions = useMemo(() => {
    const matchesCurrentFilters = (plan: Plan, excludeFilter: keyof FilterOptions) => {
      if (excludeFilter !== 'providers' && filters.providers.length > 0 && !filters.providers.includes(plan.provider as NetworkProvider)) return false;
      if (excludeFilter !== 'dataAllowances' && filters.dataAllowances.length > 0 && !filters.dataAllowances.includes(plan.data_allowance as DataAllowance)) return false;
      if (excludeFilter !== 'talkTimes' && filters.talkTimes.length > 0 && !filters.talkTimes.includes(plan.minutes as TalkTime)) return false;
      if (excludeFilter !== 'roaming' && filters.roaming.length > 0 && !filters.roaming.includes(plan.roaming as RoamingType)) return false;
      if (filters.has5G !== undefined && plan.has_5g !== filters.has5G) return false;
      if (filters.minPrice !== undefined && plan.price < filters.minPrice) return false;
      if (filters.maxPrice !== undefined && plan.price > filters.maxPrice) return false;
      return true;
    };

    const providers = new Set<NetworkProvider>();
    const dataAllowances = new Set<DataAllowance>();
    const talkTimes = new Set<TalkTime>();
    const roamingOptions = new Set<RoamingType>();
    let has5GOption = false;

    allPlans.forEach(plan => {
      if (plan.provider) providers.add(plan.provider as NetworkProvider);
      
      if (matchesCurrentFilters(plan, 'dataAllowances') && plan.data_allowance) {
        dataAllowances.add(plan.data_allowance as DataAllowance);
      }
      if (matchesCurrentFilters(plan, 'talkTimes') && plan.minutes) {
        talkTimes.add(plan.minutes as TalkTime);
      }
      if (matchesCurrentFilters(plan, 'roaming') && plan.roaming) {
        roamingOptions.add(plan.roaming as RoamingType);
      }
      if (matchesCurrentFilters(plan, 'has5G') && plan.has_5g) {
        has5GOption = true;
      }
    });

    // Convert sets to arrays and apply sorting for data allowances if the function is provided
    const dataAllowancesArray = Array.from(dataAllowances);
    const sortedDataAllowances = sortDataAllowances 
      ? sortDataAllowances(dataAllowancesArray as string[]) as DataAllowance[] 
      : dataAllowancesArray;

    // Sort talk times to show "Ubegrænset" first
    const talkTimesArray = Array.from(talkTimes);
    const sortedTalkTimes = talkTimesArray.sort((a, b) => {
      if (a === 'Ubegrænset') return -1;
      if (b === 'Ubegrænset') return 1;
      
      // Extract numeric values for comparison when possible
      const valueA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
      const valueB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
      
      return valueB - valueA; // Descending order
    });

    return {
      providers: Array.from(providers),
      dataAllowances: sortedDataAllowances,
      talkTimes: sortedTalkTimes,
      roamingOptions: Array.from(roamingOptions),
      has5GOption
    };
  }, [allPlans, filters, sortDataAllowances]);

  const handleProviderChange = (provider: NetworkProvider, checked: boolean) => {
    const updatedProviders = checked
      ? [...filters.providers, provider]
      : filters.providers.filter(p => p !== provider);
    
    onFiltersChange({
      ...filters,
      providers: updatedProviders
    });
  };
  
  const handleDataAllowanceChange = (data: DataAllowance, checked: boolean) => {
    const updatedDataAllowances = checked
      ? [...filters.dataAllowances, data]
      : filters.dataAllowances.filter(d => d !== data);
    
    onFiltersChange({
      ...filters,
      dataAllowances: updatedDataAllowances
    });
  };
  
  const handleTalkTimeChange = (talkTime: TalkTime, checked: boolean) => {
    const updatedTalkTimes = checked
      ? [...filters.talkTimes, talkTime]
      : filters.talkTimes.filter(t => t !== talkTime);
    
    onFiltersChange({
      ...filters,
      talkTimes: updatedTalkTimes
    });
  };
  
  const handleRoamingChange = (roaming: RoamingType, checked: boolean) => {
    const updatedRoaming = checked
      ? [...filters.roaming, roaming]
      : filters.roaming.filter(r => r !== roaming);
    
    onFiltersChange({
      ...filters,
      roaming: updatedRoaming
    });
  };
  
  const handle5GChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      has5G: checked ? true : undefined
    });
  };
  
  const handlePriceChange = (value: number[]) => {
    if (value.length >= 2) {
      setPriceRange([value[0], value[1]]);
      onFiltersChange({
        ...filters,
        minPrice: value[0],
        maxPrice: value[1]
      });
    }
  };
  
  const handleRemoveFilter = (filter: ActiveFilter) => {
    switch (filter.type) {
      case 'providers':
        onFiltersChange({
          ...filters,
          providers: filters.providers.filter(p => p !== filter.value)
        });
        break;
      case 'dataAllowances':
        onFiltersChange({
          ...filters,
          dataAllowances: filters.dataAllowances.filter(d => d !== filter.value)
        });
        break;
      case 'talkTimes':
        onFiltersChange({
          ...filters,
          talkTimes: filters.talkTimes.filter(t => t !== filter.value)
        });
        break;
      case 'roaming':
        onFiltersChange({
          ...filters,
          roaming: filters.roaming.filter(r => r !== filter.value)
        });
        break;
      case 'has5G':
        onFiltersChange({
          ...filters,
          has5G: undefined
        });
        break;
      case 'priceRange':
        onFiltersChange({
          ...filters,
          minPrice: 0,
          maxPrice: 500
        });
        break;
    }
  };
  
  const isPriceRangeChanged = priceRange[0] > 0 || priceRange[1] < 500;
  
  const hasActiveFilters = activeFilters.length > 0;

  // Render the horizontal filter layout for desktop
  if (displayMode === 'horizontal') {
    return (
      <div className={`bg-white rounded-lg ${className}`}>
        {hasActiveFilters && (
          <ActiveFilterTags 
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAllFilters={onReset}
          />
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {/* Price Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-between w-full">
                Pris
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h3 className="font-medium">Prisområde</h3>
                <div className="px-2">
                  <Slider
                    defaultValue={[priceRange[0], priceRange[1]]}
                    value={[priceRange[0], priceRange[1]]}
                    max={500}
                    step={10}
                    onValueChange={handlePriceChange}
                    className="mb-2"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-sm">{priceRange[0]} kr</span>
                    <span className="text-sm">{priceRange[1]}+ kr</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Provider Filter */}
          {availableOptions.providers.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between w-full">
                  Udbyder
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-60">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableOptions.providers.map((provider) => (
                    <div key={provider} className="flex items-center">
                      <Checkbox
                        id={`provider-popup-${provider}`}
                        checked={filters.providers.includes(provider)}
                        onCheckedChange={(checked) => 
                          handleProviderChange(provider, checked as boolean)
                        }
                      />
                      <Label htmlFor={`provider-popup-${provider}`} className="ml-2 text-sm">
                        {provider}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Data Allowance Filter */}
          {availableOptions.dataAllowances.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between w-full">
                  Datamængde
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-60">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableOptions.dataAllowances.map((data) => (
                    <div key={data} className="flex items-center">
                      <Checkbox
                        id={`data-popup-${data}`}
                        checked={filters.dataAllowances.includes(data)}
                        onCheckedChange={(checked) => 
                          handleDataAllowanceChange(data, checked as boolean)
                        }
                      />
                      <Label htmlFor={`data-popup-${data}`} className="ml-2 text-sm">
                        {data}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Talk Time Filter */}
          {availableOptions.talkTimes.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between w-full">
                  Taletid
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-60">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableOptions.talkTimes.map((talkTime) => (
                    <div key={talkTime} className="flex items-center">
                      <Checkbox
                        id={`talktime-popup-${talkTime}`}
                        checked={filters.talkTimes.includes(talkTime)}
                        onCheckedChange={(checked) => 
                          handleTalkTimeChange(talkTime, checked as boolean)
                        }
                      />
                      <Label htmlFor={`talktime-popup-${talkTime}`} className="ml-2 text-sm">
                        {talkTime}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        
        <CollapsibleFilters>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-2">
            {/* Roaming Filter */}
            {availableOptions.roamingOptions.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-between w-full">
                    Roaming
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-60">
                  <div className="space-y-2">
                    {availableOptions.roamingOptions.map((option) => (
                      <div key={option} className="flex items-center">
                        <Checkbox
                          id={`roaming-popup-${option}`}
                          checked={filters.roaming.includes(option)}
                          onCheckedChange={(checked) => 
                            handleRoamingChange(option, checked as boolean)
                          }
                        />
                        <Label htmlFor={`roaming-popup-${option}`} className="ml-2 text-sm">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* 5G Filter */}
            {availableOptions.has5GOption && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-between w-full">
                    5G Netværk
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-60">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Checkbox
                        id="5g-support-popup"
                        checked={filters.has5G === true}
                        onCheckedChange={(checked) => handle5GChange(checked as boolean)}
                      />
                      <Label htmlFor="5g-support-popup" className="ml-2 text-sm">
                        5G Understøttelse
                      </Label>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CollapsibleFilters>
      </div>
    );
  }

  // Render the original sidebar layout for mobile
  return (
    <div className={`rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Filtre</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-8 text-xs flex items-center text-muted-foreground hover:text-foreground"
            aria-label="Nulstil alle filtre"
          >
            <FilterX className="h-3.5 w-3.5 mr-1" />
            Nulstil
          </Button>
        )}
      </div>
      
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3" id="price-range-label">Prisområde</h4>
        <div className="px-2">
          <Slider
            defaultValue={[priceRange[0], priceRange[1]]}
            value={[priceRange[0], priceRange[1]]}
            max={500}
            step={10}
            onValueChange={handlePriceChange}
            className="mb-2"
            aria-labelledby="price-range-label"
          />
          <div className="flex justify-between items-center">
            <span className="text-sm">{priceRange[0]} kr</span>
            <span className="text-sm">{priceRange[1]}+ kr</span>
          </div>
        </div>
      </div>
      
      {availableOptions.providers.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3" id="providers-group">Udbyder</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto pr-2" role="group" aria-labelledby="providers-group">
            {availableOptions.providers.map((provider) => (
              <div key={provider} className="flex items-center">
                <Checkbox
                  id={`provider-${provider}`}
                  checked={filters.providers.includes(provider)}
                  onCheckedChange={(checked) => 
                    handleProviderChange(provider, checked as boolean)
                  }
                  aria-labelledby={`provider-label-${provider}`}
                />
                <Label
                  htmlFor={`provider-${provider}`}
                  className="ml-2 text-sm font-normal cursor-pointer"
                  id={`provider-label-${provider}`}
                >
                  {provider}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {availableOptions.dataAllowances.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3" id="data-group">Datamængde</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto pr-2" role="group" aria-labelledby="data-group">
            {availableOptions.dataAllowances.map((data) => (
              <div key={data} className="flex items-center">
                <Checkbox
                  id={`data-${data}`}
                  checked={filters.dataAllowances.includes(data)}
                  onCheckedChange={(checked) => 
                    handleDataAllowanceChange(data, checked as boolean)
                  }
                  aria-labelledby={`data-label-${data}`}
                />
                <Label
                  htmlFor={`data-${data}`}
                  className="ml-2 text-sm font-normal cursor-pointer"
                  id={`data-label-${data}`}
                >
                  {data}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {availableOptions.talkTimes.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3" id="talktime-group">Taletid</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto pr-2" role="group" aria-labelledby="talktime-group">
            {availableOptions.talkTimes.map((talkTime) => (
              <div key={talkTime} className="flex items-center">
                <Checkbox
                  id={`talktime-${talkTime}`}
                  checked={filters.talkTimes.includes(talkTime)}
                  onCheckedChange={(checked) => 
                    handleTalkTimeChange(talkTime, checked as boolean)
                  }
                  aria-labelledby={`talktime-label-${talkTime}`}
                />
                <Label
                  htmlFor={`talktime-${talkTime}`}
                  className="ml-2 text-sm font-normal cursor-pointer"
                  id={`talktime-label-${talkTime}`}
                >
                  {talkTime}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {availableOptions.has5GOption && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3" id="5g-group">5G Understøttelse</h4>
          <div className="flex items-center" role="group" aria-labelledby="5g-group">
            <Checkbox
              id="5g-support"
              checked={filters.has5G === true}
              onCheckedChange={(checked) => handle5GChange(checked as boolean)}
              aria-labelledby="5g-support-label"
            />
            <Label
              htmlFor="5g-support"
              className="ml-2 text-sm font-normal cursor-pointer"
              id="5g-support-label"
            >
              5G Netværk
            </Label>
          </div>
        </div>
      )}
      
      {availableOptions.roamingOptions.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-3" id="roaming-group">Roaming</h4>
          <div className="space-y-2" role="group" aria-labelledby="roaming-group">
            {availableOptions.roamingOptions.map((option) => (
              <div key={option} className="flex items-center">
                <Checkbox
                  id={`roaming-${option}`}
                  checked={filters.roaming.includes(option)}
                  onCheckedChange={(checked) => 
                    handleRoamingChange(option, checked as boolean)
                  }
                  aria-labelledby={`roaming-label-${option}`}
                />
                <Label
                  htmlFor={`roaming-${option}`}
                  className="ml-2 text-sm font-normal cursor-pointer"
                  id={`roaming-label-${option}`}
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <Button 
        className="w-full md:hidden"
        aria-label="Anvend filtre"
      >
        <Search className="h-4 w-4 mr-2" />
        Anvend filtre
      </Button>
    </div>
  );
};
